import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  // True during the one-shot refresh-on-reload flow. While true, ProtectedRoute
  // holds a skeleton instead of redirecting to /login or firing API calls.
  isBootstrapping: boolean;
  setAccessToken: (token: string) => void;
  setAuth: (token: string, user: AuthUser) => void;
  logout: () => void;
  bootstrap: () => Promise<void>;
}

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '';

let bootstrapPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      // Start in bootstrapping state only if a user was persisted from a prior
      // session — otherwise there's nothing to rehydrate.
      isBootstrapping: false,
      setAccessToken: (accessToken) => set({ accessToken, isAuthenticated: true }),
      setAuth: (accessToken, user) =>
        set({ accessToken, user, isAuthenticated: true, isBootstrapping: false }),
      logout: () => {
        bootstrapPromise = null;
        set({ accessToken: null, user: null, isAuthenticated: false, isBootstrapping: false });
      },
      bootstrap: () => {
        if (bootstrapPromise) return bootstrapPromise;

        const { user, accessToken } = get();
        // Nothing persisted, or already have a token — no bootstrap needed.
        if (!user || accessToken) {
          set({ isBootstrapping: false });
          return Promise.resolve();
        }

        set({ isBootstrapping: true });

        bootstrapPromise = (async () => {
          try {
            const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
              method: 'POST',
              credentials: 'include',
            });
            if (!res.ok) {
              // Refresh cookie missing or invalid — drop the persisted user
              // and let ProtectedRoute redirect to /login.
              set({ user: null, accessToken: null, isAuthenticated: false, isBootstrapping: false });
              return;
            }
            const data = (await res.json()) as { accessToken: string };
            set({ accessToken: data.accessToken, isAuthenticated: true, isBootstrapping: false });
          } catch {
            set({ user: null, accessToken: null, isAuthenticated: false, isBootstrapping: false });
          } finally {
            bootstrapPromise = null;
          }
        })();

        return bootstrapPromise;
      },
    }),
    {
      name: 'accucore-auth',
      // Access token deliberately NOT persisted (avoid XSS leakage). The refresh
      // cookie + bootstrap() rehydrates it on reload.
      partialize: (state) => ({ user: state.user }),
      // Users rehydrated from storage need a bootstrap pass before their first API call.
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          state.isBootstrapping = true;
        }
      },
    },
  ),
);
